import { Link } from "react-router";
import type { PlaySessionSummary } from "@storyai/shared";
import { SessionRow } from "../components/sessions";
import { EmptyState, ErrorPanel, Loading, PageHeader } from "../components/ui";
import { useMySessions } from "../lib/queries";

interface StoryGroup {
  storyId: string;
  storyTitle: string;
  storyCoverUrl: string | null;
  sessions: PlaySessionSummary[];
}

/** Groups by story, preserving the order of first appearance (lists arrive lastPlayedAt desc). */
function groupByStory(sessions: PlaySessionSummary[]): StoryGroup[] {
  const groups: StoryGroup[] = [];
  const index = new Map<string, StoryGroup>();
  for (const s of sessions) {
    let g = index.get(s.storyId);
    if (!g) {
      g = { storyId: s.storyId, storyTitle: s.storyTitle, storyCoverUrl: s.storyCoverUrl, sessions: [] };
      index.set(s.storyId, g);
      groups.push(g);
    }
    g.sessions.push(s);
  }
  return groups;
}

export function PlayingPage() {
  const q = useMySessions();

  return (
    <div className="page">
      <PageHeader title="Playing" subtitle="Every session you have, across all stories." />

      {q.isPending ? (
        <Loading />
      ) : q.isError ? (
        <ErrorPanel error={q.error} />
      ) : q.data.sessions.length === 0 ? (
        <EmptyState title="No sessions yet.">
          Pick something from <Link to="/stories">Public Stories</Link> and start a session. Every
          story you play will be listed here, whether it's public, unlisted, or one of your own.
        </EmptyState>
      ) : (
        <div className="session-groups">
          {groupByStory(q.data.sessions).map((g) => (
            <section key={g.storyId} className="session-group">
              <Link to={`/stories/${g.storyId}`} className="session-group-head">
                {g.storyCoverUrl ? (
                  <img className="thumb" src={g.storyCoverUrl} alt="" />
                ) : (
                  <div className="thumb thumb-placeholder" aria-hidden="true">
                    {g.storyTitle.slice(0, 1).toUpperCase()}
                  </div>
                )}
                <h2>
                  {g.storyTitle}
                  {g.sessions[0]?.versionTitle && (
                    <span className="muted"> · {g.sessions[0].versionTitle}</span>
                  )}
                </h2>
              </Link>
              <ul className="session-list">
                {g.sessions.map((s) => (
                  <SessionRow key={s.id} session={s} />
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
